import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FlaskConical, Truck, Award, ArrowRight, Dumbbell, Brain, Repeat, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import ProductCard from "@/components/ProductCard";
import EmailPopup from "@/components/EmailPopup";
import { groupProducts } from "@/lib/group";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/best-sellers").then(({ data }) => setBestSellers(data)).catch(() => {});
  }, []);

  return (
    <div>
      <EmailPopup />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ape-border">
        <div className="absolute inset-0">
          <img src={BRAND.heroGorilla} alt="" className="w-full h-full object-cover object-right opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-ape-bg via-ape-bg/85 to-ape-bg/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-36">
          <div className="max-w-2xl fade-up">
            <span className="inline-block font-mono2 text-xs tracking-[0.3em] text-ape-red border border-ape-red px-3 py-1.5 mb-6">
              PRIMAL STRENGTH · MODERN SCIENCE
            </span>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
              BUILT<br />DIFFERENT.<br /><span className="text-ape-red">EVOLVE BEYOND<br />LIMITS.</span>
            </h1>
            <p className="text-ape-muted text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
              Premium research peptides engineered for those who refuse to settle for average. Third-party tested. 99%+ purity. Fuel the beast within.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="group bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-8 py-4 flex items-center gap-2 transition-colors duration-150">
                Shop Peptides <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-150" />
              </Link>
              <Link to="/verify" data-testid="hero-verify-btn"
                className="border-2 border-ape-red text-ape-red hover:bg-ape-red hover:text-white font-head uppercase tracking-widest px-8 py-4 transition-colors duration-150">
                Verify a Batch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-ape-surface border-b border-ape-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Third-Party Lab Tested", s: "Independent purity verification" },
            { icon: FlaskConical, t: "99%+ Purity", s: "HPLC & mass-spec validated" },
            { icon: Truck, t: "Discreet Shipping", s: "Secure, fast & confidential" },
            { icon: Award, t: "Premium Packaging", s: "USA-based, batch coded" },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <b.icon className="text-ape-red shrink-0 mt-1" size={28} />
              <div>
                <div className="font-head uppercase text-sm tracking-wide leading-tight">{b.t}</div>
                <div className="text-ape-muted text-xs mt-1">{b.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS — category shelves */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 space-y-20">
        {bestSellers.length > 0 && (
          <div data-testid="home-shelf-bestsellers">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red flex items-center gap-2">
                  <TrendingUp size={14} /> TOP MOVERS
                </span>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Best Sellers</h2>
              </div>
              <Link to="/shop" className="font-head uppercase tracking-widest text-sm text-ape-red hover:text-white flex items-center gap-2 transition-colors duration-150" data-testid="shelf-viewall-bestsellers">
                View All <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bestSellers.map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
            </div>
          </div>
        )}
        {[
          { label: "Weight & Metabolic", cat: "Metabolic", tag: "BURN · BUILD · BALANCE" },
          { label: "Recovery", cat: "Recovery", tag: "REPAIR · REBUILD · RETURN" },
          { label: "Blends", cat: "Blends", tag: "STACKED · SYNERGISTIC" },
        ].map((shelf) => {
          const items = groupProducts(products.filter((p) => p.category === shelf.cat)).slice(0, 3);
          if (items.length === 0) return null;
          return (
            <div key={shelf.cat} data-testid={`home-shelf-${shelf.cat.toLowerCase()}`}>
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">{shelf.tag}</span>
                  <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">{shelf.label}</h2>
                </div>
                <Link to="/shop" className="font-head uppercase tracking-widest text-sm text-ape-red hover:text-white flex items-center gap-2 transition-colors duration-150" data-testid={`shelf-viewall-${shelf.cat.toLowerCase()}`}>
                  View All <ArrowRight size={16} />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((g, i) => <ProductCard key={g.key} p={g.rep} variants={g.variants} index={i} />)}
              </div>
            </div>
          );
        })}
        <div className="text-center pt-4">
          <Link to="/shop" className="inline-flex bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-10 py-4 transition-colors duration-150" data-testid="home-shop-all">
            Shop All Peptides <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>

      {/* MINDSET */}
      <section className="relative border-y border-ape-border overflow-hidden">
        <img src={BRAND.workout} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-ape-bg/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">THE APE MINDSET</span>
          <h2 className="font-display text-4xl sm:text-6xl tracking-tight mt-3 max-w-3xl">
            DISCIPLINE OVER MOTIVATION. <span className="text-ape-red">CONSISTENCY OVER COMFORT.</span>
          </h2>
          <p className="text-ape-muted text-lg mt-5 max-w-2xl">
            Growth isn't given — it's earned. Every day is a chance to become stronger than yesterday. We build the tools; you bring the relentless pursuit.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {[
              { icon: Dumbbell, t: "Strength", s: "Push past every limit you thought you had." },
              { icon: Repeat, t: "Consistency", s: "Show up daily. Compounding wins the game." },
              { icon: Brain, t: "Discipline", s: "Master yourself before you master anything else." },
              { icon: TrendingUp, t: "Evolution", s: "Never plateau. Always evolve beyond limits." },
            ].map((c, i) => (
              <div key={i} className="bg-ape-surface border border-ape-border p-6">
                <c.icon className="text-ape-red" size={30} />
                <h3 className="font-display text-2xl tracking-wide mt-4">{c.t}</h3>
                <p className="text-ape-muted text-sm mt-2">{c.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QA / LAB */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative border border-ape-border overflow-hidden aspect-[4/3]">
          <img src={BRAND.lab} alt="Laboratory testing" className="w-full h-full object-cover" />
          <div className="absolute bottom-4 left-4 bg-ape-red text-black font-mono2 text-xs font-bold px-3 py-2 tracking-widest">
            HPLC VERIFIED · 99%+ PURITY
          </div>
        </div>
        <div>
          <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">QUALITY ASSURANCE</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-3">TESTED. VERIFIED. TRUSTED.</h2>
          <p className="text-ape-muted mt-4 leading-relaxed">
            Every single batch is independently analyzed by third-party laboratories for purity, identity, and sterility. Scan or enter your batch code to view the full certificate of analysis.
          </p>
          <ul className="mt-6 space-y-3">
            {["HPLC purity analysis on every batch", "Third-party sterility & endotoxin testing", "Batch-coded vials with QR verification", "Premium lyophilized powder, USA sourced"].map((t, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <ShieldCheck className="text-ape-red shrink-0" size={18} /> {t}
              </li>
            ))}
          </ul>
          <Link to="/verify" className="inline-flex mt-8 bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-8 py-4 transition-colors duration-150" data-testid="qa-verify-btn">
            Verify Your Batch
          </Link>
        </div>
      </section>
    </div>
  );
}
